import { createTheme } from '@mui/material/styles';
import tokens from './tokens.json';

// ============================================================================
// COFFEEMATES DESIGN SYSTEM v3.0 — "Neon Dusk"
//
// Dark, bold, Gen-Z energy. Warm coral-to-rose gradients on deep charcoal.
// Designed for young Baku professionals who socialize at night.
//
// SOURCE OF TRUTH: ./tokens.json (shared with mobile via require()).
// All values here derive from tokens.json — do NOT hardcode hexes.
// ============================================================================

const t = tokens;

export const colors = {
  primary: {
    main:        t.colors.primary.main,
    light:       t.colors.primary.light,
    dark:        t.colors.primary.dark,
    subtle:      t.colors.primary.subtle,
    transparent: t.colors.primary.transparent,
    gradient:    t.colors.primary.gradient,
  },
  secondary: {
    main:        t.colors.secondary.main,
    light:       t.colors.secondary.light,
    dark:        t.colors.secondary.dark,
    subtle:      t.colors.secondary.subtle,
    transparent: t.colors.secondary.transparent,
  },
  accent: {
    main:   t.colors.accent.main,
    light:  t.colors.accent.light,
    dark:   t.colors.accent.dark,
    subtle: t.colors.accent.subtle,
  },
  success: { main: t.colors.success.main, light: t.colors.success.light, dark: t.colors.success.dark, subtle: t.colors.success.subtle },
  warning: { main: t.colors.warning.main, light: t.colors.warning.light, dark: t.colors.warning.dark, subtle: t.colors.warning.subtle },
  error:   { main: t.colors.error.main,   light: t.colors.error.light,   dark: t.colors.error.dark,   subtle: t.colors.error.subtle   },
  info:    { main: t.colors.info.main,    light: t.colors.info.light,    dark: t.colors.info.dark,    subtle: t.colors.info.subtle    },
  neutral: {
    50:  t.colors.neutral['50'],
    100: t.colors.neutral['100'],
    200: t.colors.neutral['200'],
    300: t.colors.neutral['300'],
    400: t.colors.neutral['400'],
    500: t.colors.neutral['500'],
    600: t.colors.neutral['600'],
    700: t.colors.neutral['700'],
    800: t.colors.neutral['800'],
    900: t.colors.neutral['900'],
  },
  professional: t.colors.professional,
  social:       t.colors.social,
  admin: {
    primary: t.colors.admin.primary,
    light:   t.colors.admin.light,
    dark:    t.colors.admin.dark,
  },
  background: {
    default: t.colors.background.default,
    paper:   t.colors.background.paper,
    surface: t.colors.background.surface,
  },
};

// ============================================================================
// SHADOWS — Warm glow on dark (derived from tokens.json)
// ============================================================================
const softShadow = {
  xs:         t.shadow.xs,
  sm:         t.shadow.sm,
  md:         t.shadow.md,
  lg:         t.shadow.lg,
  xl:         t.shadow.xl,
  glow:       t.shadow.glow,
  glowStrong: t.shadow.glowStrong,
  focus:      t.shadow.focus,
};

// ============================================================================
// THEME
// ============================================================================

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: '#FFFFFF',
    },
    success: { main: colors.success.main, light: colors.success.light, dark: colors.success.dark },
    warning: { main: colors.warning.main, light: colors.warning.light, dark: colors.warning.dark },
    error: { main: colors.error.main, light: colors.error.light, dark: colors.error.dark },
    info: { main: colors.info.main, light: colors.info.light, dark: colors.info.dark },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.neutral[900],
      secondary: colors.neutral[500],
    },
    divider: colors.neutral[200],
  },

  // ── Typography — Modern, bold, high contrast (derived from tokens.json) ──
  typography: {
    fontFamily: t.fontFamily.body,
    h1: {
      fontFamily: t.fontFamily.heading,
      fontWeight: t.typography.h1.fontWeight,
      fontSize: `${t.typography.h1.fontSize}rem`,
      letterSpacing: t.typography.h1.letterSpacing,
      lineHeight: t.typography.h1.lineHeight,
      color: '#FFFFFF',
    },
    h2: {
      fontFamily: t.fontFamily.heading,
      fontWeight: t.typography.h2.fontWeight,
      fontSize: `${t.typography.h2.fontSize}rem`,
      letterSpacing: t.typography.h2.letterSpacing,
      lineHeight: t.typography.h2.lineHeight,
      color: '#FFFFFF',
    },
    h3: {
      fontFamily: t.fontFamily.heading,
      fontWeight: t.typography.h3.fontWeight,
      fontSize: `${t.typography.h3.fontSize}rem`,
      letterSpacing: t.typography.h3.letterSpacing,
      lineHeight: t.typography.h3.lineHeight,
      color: '#FFFFFF',
    },
    h4: {
      fontFamily: t.fontFamily.heading,
      fontWeight: t.typography.h4.fontWeight,
      fontSize: `${t.typography.h4.fontSize}rem`,
      letterSpacing: t.typography.h4.letterSpacing,
      lineHeight: t.typography.h4.lineHeight,
      color: '#FFFFFF',
    },
    h5: {
      fontFamily: t.fontFamily.heading,
      fontWeight: t.typography.h5.fontWeight,
      fontSize: `${t.typography.h5.fontSize}rem`,
      lineHeight: t.typography.h5.lineHeight,
      color: '#FFFFFF',
    },
    h6: {
      fontFamily: t.fontFamily.heading,
      fontWeight: t.typography.h6.fontWeight,
      fontSize: `${t.typography.h6.fontSize}rem`,
      lineHeight: t.typography.h6.lineHeight,
      color: '#FFFFFF',
    },
    body1:   { fontSize: `${t.typography.body1.fontSize}rem`,   lineHeight: t.typography.body1.lineHeight,   letterSpacing: t.typography.body1.letterSpacing,   color: colors.neutral[900] },
    body2:   { fontSize: `${t.typography.body2.fontSize}rem`,   lineHeight: t.typography.body2.lineHeight,   letterSpacing: t.typography.body2.letterSpacing,   color: colors.neutral[500] },
    caption: { fontSize: `${t.typography.caption.fontSize}rem`, lineHeight: t.typography.caption.lineHeight, letterSpacing: t.typography.caption.letterSpacing, color: colors.neutral[400] },
    button:   { textTransform: 'none' as const, fontWeight: 600, letterSpacing: '0.01em' },
    overline: { textTransform: 'uppercase' as const, fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.1em' },
  },

  shape: {
    borderRadius: 14,
  },

  components: {
    // ── CssBaseline — global resets + prefers-reduced-motion guard ──
    // Reduced-motion users get all transitions/animations/scroll-behavior
    // collapsed to ~0ms; `!important` defeats per-component inline
    // `transition` overrides. Contract: DESIGN_SYSTEM.md §Motion.
    MuiCssBaseline: {
      styleOverrides: {
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    // ── Buttons — Bold gradient, glow on hover ──
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 28px',
          fontSize: '0.95rem',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          background: colors.primary.gradient,
          color: '#FFFFFF',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
            boxShadow: softShadow.glow,
          },
        },
        outlinedPrimary: {
          borderColor: 'rgba(255, 255, 255, 0.12)',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: colors.primary.main,
            backgroundColor: colors.primary.transparent,
          },
        },
        textPrimary: {
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.transparent,
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1.05rem',
          borderRadius: 14,
        },
      },
    },

    // ── Cards — Glass-dark, glowing edges on hover ──
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          boxShadow: 'none',
          border: `1px solid rgba(255, 255, 255, 0.06)`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            borderColor: 'rgba(232, 135, 92, 0.2)',
          },
        },
      },
    },

    // ── Chips — Glass badges ──
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.8rem',
          height: 32,
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          color: '#FFFFFF',
          backdropFilter: 'blur(8px)',
        },
        sizeSmall: {
          height: 26,
          fontSize: '0.7rem',
        },
      },
    },

    // ── Text Fields — Dark glass, coral focus ──
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.08)',
              transition: 'border-color 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
            },
            '&.Mui-focused': {
              boxShadow: softShadow.focus,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.neutral[500],
          },
          '& .MuiOutlinedInput-input': {
            color: colors.neutral[900],
          },
        },
      },
    },

    // ── App Bar — Dark glass, no border ──
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(15, 15, 19, 0.85)',
          backdropFilter: 'blur(24px)',
          color: '#FFFFFF',
          boxShadow: 'none',
          borderBottom: `1px solid rgba(255, 255, 255, 0.06)`,
        },
      },
    },

    // ── Paper — Dark surface ──
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
        },
        elevation0: {
          boxShadow: 'none',
          border: `1px solid rgba(255, 255, 255, 0.06)`,
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
        },
        elevation1: {
          boxShadow: softShadow.sm,
          backgroundColor: colors.background.surface,
        },
      },
    },

    // ── Dialog — Dark glass ──
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '24px',
          padding: '8px',
          backgroundColor: colors.background.surface,
          border: `1px solid rgba(255, 255, 255, 0.08)`,
          backgroundImage: 'none',
        },
      },
    },

    // ── Toggle Buttons — Dark pill selector ──
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          borderRadius: 12,
          padding: 3,
          border: `1px solid rgba(255, 255, 255, 0.06)`,
          gap: 2,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          border: 'none !important',
          padding: '6px 16px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.85rem',
          color: colors.neutral[500],
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            backgroundColor: `rgba(232, 135, 92, 0.15) !important`,
            color: colors.primary.main,
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
          },
        },
      },
    },

    // ── Bottom Navigation — Dark glass ──
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
          backgroundColor: 'rgba(15, 15, 19, 0.92)',
          backdropFilter: 'blur(24px)',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: colors.neutral[400],
          '&.Mui-selected': {
            color: colors.primary.main,
          },
          minWidth: 'auto',
          padding: '6px 0',
        },
      },
    },

    // ── Linear Progress — Rounded, vivid ──
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },

    // ── Skeleton — Dark pulse ──
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          '&::after': {
            background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)`,
          },
        },
      },
    },

    // ── Avatar — Gradient background ──
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: `2px solid ${colors.background.default}`,
          background: colors.primary.gradient,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: '0.75rem',
        },
      },
    },

    // ── Alert — Dark glass with color accent ──
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },

    // ── Divider ──
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.06)',
        },
      },
    },

    // ── Select ──
    MuiSelect: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
        },
      },
    },

    // ── Menu ──
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.surface,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundImage: 'none',
        },
      },
    },

    // ── MenuItem ──
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: colors.primary.transparent,
          },
        },
      },
    },
  },
});

// ============================================================================
// DESIGN TOKENS — Export for direct use in components (derived from tokens.json)
// ============================================================================
export const designTokens = {
  shadow: softShadow,
  radius: {
    sm:   t.radius.sm,
    md:   t.radius.md,
    lg:   t.radius.lg,
    xl:   t.radius.xl,
    full: t.radius.full,
  },
  transition: {
    fast:   t.transition.fast,
    normal: t.transition.normal,
    slow:   t.transition.slow,
    spring: t.transition.spring,
  },
  spacing: {
    page:    t.spacing.page,
    section: t.spacing.section,
    card:    t.spacing.card,
  },
  glass: {
    surface:     t.glass.surface,
    border:      t.glass.border,
    borderHover: t.glass.borderHover,
    blur:        t.glass.blur,
  },
};

// ============================================================================
// MOTION TOKENS — spec: DESIGN_SYSTEM.md §Motion; values from tokens.json
// (mobile mirrors them). Standard hover lift: translateY(-4px) +
// shadow.lg + 250ms. Anything longer than motion.duration.fast MUST
// collapse to `instant` under reduced-motion — the MuiCssBaseline @media
// override handles CSS automatically; use `prefersReducedMotion()` only
// for JS-driven animation (rAF, react-spring) that CSS can't see.
// ============================================================================
export const motion = {
  duration: {
    instant: t.motion.duration.instant,
    fast:    t.motion.duration.fast,
    base:    t.motion.duration.base,
    slow:    t.motion.duration.slow,
  },
  easing: {
    standard: t.motion.easing.standard,
    enter:    t.motion.easing.enter,
    exit:     t.motion.easing.exit,
  },
  hover: {
    lift:   t.motion.hover.lift,   // -4 (px) — standard card-lift on hover
    shadow: t.motion.hover.shadow, // 'lg' — designTokens.shadow.lg
  },
};

/**
 * Returns true if the user has opted into reduced motion.
 * SSR-safe: returns false when `window` is undefined.
 *
 * Usage:
 *   const reduced = prefersReducedMotion();
 *   sx={{ transition: reduced ? motion.duration.instant : motion.duration.base }}
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // expected: matchMedia exists but throws on the query (very rare; mostly
    // legacy browsers or test envs). Fall through to "no preference" default.
    return false;
  }
}

export default theme;
